// Import Dependencies
import PropTypes from "prop-types";
import { TbPinned, TbPinnedOff } from "react-icons/tb";
import clsx from "clsx";
import { useMediaQuery } from "hooks/useMediaQuery";

// Local Imports
import { Button, Checkbox, Switch } from "components/ui";

// ----------------------------------------------------------------------

// Converts uppercase strings like "ASSIGNEE 1" → "Assignee 1"
const toTitleCase = (str) =>
  str
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const getColumnLabel = (column) => {
  const raw = column?.columnDef?.label || column?.columnDef?.header || column.id;
  // Only apply title-case conversion if the raw value is a plain string (not a render function)
  if (typeof raw === "string") return toTitleCase(raw);
  return raw;
};

export function TableSettings({ table }) {
  const isMobile = useMediaQuery("(max-width: 767px)");
  const tableSettings = table.getState().tableSettings;
  const setTableSettings = table.options.meta.setTableSettings;

  const configurableColumns = [...table.getAllLeafColumns()].filter(
    (column) => !column.columnDef?.isHiddenColumn && !column.columnDef?.meta?.alwaysVisible
  );

  const hasUnselectedColumn = configurableColumns.some((column) => !column.getIsVisible());

  return (
    <>
      {Object.keys(tableSettings).length > 0 && (
        <div className="mb-4 mt-3 flex flex-col items-start space-y-2 px-3 text-gray-600 dark:text-dark-100">
          {Object.prototype.hasOwnProperty.call(
            tableSettings,
            "enableFullScreen",
          ) && (
              <Switch
                label="Full Screen"
                checked={tableSettings.enableFullScreen}
                onChange={(e) =>
                  setTableSettings((state) => ({
                    ...state,
                    enableFullScreen: e.target.checked,
                  }))
                }
                className="h-4 w-8"
              />
            )}
          {Object.prototype.hasOwnProperty.call(
            tableSettings,
            "enableSorting",
          ) && (
              <Switch
                label="Sort"
                checked={tableSettings.enableSorting}
                onChange={(e) => {
                  setTableSettings((state) => ({
                    ...state,
                    enableSorting: e.target.checked,
                  }));
                  table.resetSorting();
                }}
                className="h-4 w-8"
              />
            )}
          {/* row dense */}
          {Object.prototype.hasOwnProperty.call(
            tableSettings,
            "enableRowDense",
          ) && (
              <Switch
                label="Row Dense"
                checked={tableSettings.enableRowDense}
                onChange={(e) =>
                  setTableSettings((state) => ({
                    ...state,
                    enableRowDense: e.target.checked,
                  }))
                }
                className="h-4 w-8"
              />
            )}
          {/* col dense */}
          {Object.prototype.hasOwnProperty.call(
            tableSettings,
            "enableColDense",
          ) && (
              <Switch
                label="Col Dense"
                checked={tableSettings.enableColDense}
                onChange={(e) =>
                  setTableSettings((state) => ({
                    ...state,
                    enableColDense: e.target.checked,
                  }))
                }
                className="h-4 w-8"
              />
            )}
        </div>
      )}

      <div className="flex items-center space-x-2 px-3 ">
        <p className="text-tiny uppercase">column visibility</p>
        <hr className="flex-1 border-gray-300 dark:border-dark-500" />
      </div>

      <div className="mt-3 flex flex-1 min-h-0 max-h-[40vh] flex-col space-y-2 overflow-y-auto overscroll-y-contain px-3 pb-3 text-gray-600 dark:text-dark-100">
        {configurableColumns
          .sort((a, b) => {
            const aPinned = !!a.getIsPinned();
            const bPinned = !!b.getIsPinned();
            if (aPinned && !bPinned) return -1;
            if (!aPinned && bPinned) return 1;
            return 0;
          })
          .map((column) => (
            <div
              className="flex min-w-0 items-center justify-between ltr:-mr-2 rtl:-ml-2"
              key={column.id}
            >
              <Checkbox
                label={getColumnLabel(column)}
                checked={column.getIsVisible()}
                onChange={column.getToggleVisibilityHandler()}
                className="size-4.5 shrink-0"
                classNames={{ label: "min-w-0 flex-1", labelText: "truncate" }}
                labelProps={{ title: getColumnLabel(column) }}
              />
              {!isMobile && column.getCanPin() &&
                (column.getIsPinned() ? (
                  <Button
                    onClick={() => column.pin(false)}
                    variant="flat"
                    className="size-6 rounded-full"
                    isIcon
                    title="UnPin Column"
                    aria-label="UnPin Column"
                  >
                    <TbPinnedOff className="size-4" />
                  </Button>
                ) : (
                  <div className="flex">
                    <Button
                      onClick={() => {
                        column.pin("left");
                      }}
                      variant="flat"
                      className="size-6 rounded-full rtl:rotate-180"
                      isIcon
                      title="Pin Left"
                      aria-label="Pin Left"
                    >
                      <TbPinned className="size-4 rotate-90" />
                    </Button>

                    <Button
                      onClick={() => {
                        column.pin("right");
                      }}
                      variant="flat"
                      className="size-6 -rotate-90 rounded-full"
                      isIcon
                      title="Pin Right"
                      aria-label="Pin Right"
                    >
                      <TbPinned className="size-4 rtl:rotate-180" />
                    </Button>
                  </div>
                ))}
            </div>
          ))}
      </div>

      <div className="flex w-full shrink-0 border-t border-gray-300 dark:border-dark-500">
        <Button
          variant="flat"
          className={clsx(
            "h-9 text-xs-plus leading-none hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors",
            hasUnselectedColumn
              ? "flex-1 rounded-none rounded-bl-lg border-r border-gray-300 dark:border-dark-500"
              : "w-full rounded-none rounded-b-lg"
          )}
          onClick={() => {
            const visibility = {};
            table.getAllLeafColumns().forEach(col => {
              if (col.columnDef.meta?.isDefault !== false) {
                visibility[col.id] = true;
              } else {
                visibility[col.id] = false;
              }
            });
            table.setColumnVisibility(visibility);
            if (table.setColumnOrder) {
              table.setColumnOrder([]);
            }
            if (table.resetColumnSizing) {
              table.resetColumnSizing();
            }
          }}
        >
          Reset to Default
        </Button>
        {hasUnselectedColumn && (
          <Button
            variant="flat"
            className="h-9 flex-1 rounded-none rounded-br-lg text-xs-plus leading-none hover:bg-gray-100 dark:hover:bg-dark-600 transition-colors"
            onClick={() => {
              const visibility = {};
              table.getAllLeafColumns().forEach(col => {
                visibility[col.id] = true;
              });
              table.setColumnVisibility(visibility);
            }}
          >
            Show All
          </Button>
        )}
      </div>
    </>
  );
}

TableSettings.propTypes = {
  table: PropTypes.object,
};

